import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { MapPin, Phone, Building, ExternalLink } from 'lucide-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const SalonCarousel = ({ salons }) => {
  const { settings } = useSettings();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      skipSnaps: false,
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  if (!salons || salons.length === 0) return null;

  return (
    <div className="relative">
      {/* Carousel Container */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {salons.map((salon) => (
            <div 
              key={salon._id} 
              className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
            >
              <Link 
                to={`/salonlarimiz/${salon.slug || salon._id}`}
                className="block"
              >
                <div 
                  className="rounded-lg overflow-hidden transition-all group h-full"
                  style={{
                    background: `linear-gradient(to bottom right, ${settings?.gradient_salon_from || '#10b981'}33, ${settings?.gradient_salon_to || '#f59e0b'}33)`,
                    borderColor: `${settings?.gradient_salon_from || '#10b981'}4d`,
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `linear-gradient(to bottom right, ${settings?.gradient_salon_from || '#10b981'}4d, ${settings?.gradient_salon_to || '#f59e0b'}4d)`;
                    e.currentTarget.style.borderColor = `${settings?.gradient_salon_to || '#f59e0b'}80`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = `linear-gradient(to bottom right, ${settings?.gradient_salon_from || '#10b981'}33, ${settings?.gradient_salon_to || '#f59e0b'}33)`;
                    e.currentTarget.style.borderColor = `${settings?.gradient_salon_from || '#10b981'}4d`;
                  }}
                >
                  <div className="flex flex-col md:flex-row h-full">
                    {/* Sol: Bilgiler */}
                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 
                          className="text-white font-bold text-xl mb-2 transition-all duration-300"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${settings?.gradient_salon_from || '#10b981'}, ${settings?.gradient_salon_to || '#f59e0b'})`;
                            e.currentTarget.style.webkitBackgroundClip = 'text';
                            e.currentTarget.style.webkitTextFillColor = 'transparent';
                            e.currentTarget.style.backgroundClip = 'text';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundImage = '';
                            e.currentTarget.style.webkitTextFillColor = 'white';
                          }}
                        >
                          {salon.name}
                        </h3>
                        <div className="space-y-2 text-gray-300 text-sm">
                          <div className="flex items-center gap-2">
                            <MapPin size={16} style={{ color: settings?.gradient_salon_from || '#10b981' }} className="flex-shrink-0" />
                            <span className="line-clamp-2">{salon.city}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Building size={16} style={{ color: settings?.gradient_salon_from || '#10b981' }} className="flex-shrink-0" />
                            <span>{salon.halls_count} Salon</span>
                          </div>
                          {salon.phone && (
                            <div className="flex items-center gap-2">
                              <Phone size={16} style={{ color: settings?.gradient_salon_from || '#10b981' }} className="flex-shrink-0" />
                              <span>{salon.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {salon.map_url && (
                        <div className="mt-4">
                          <a
                            href={salon.map_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                            style={{
                              color: settings?.gradient_salon_to || '#f59e0b'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = settings?.gradient_salon_to || '#f59e0b';
                            }}
                          >
                            <ExternalLink size={16} />
                            Haritada Gör
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Sağ: Görsel */}
                    {salon.image && (
                      <div className="md:w-48 lg:w-56 h-48 md:h-auto">
                        <img 
                          src={salon.image}
                          alt={salon.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x300?text=Salon';
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons - Hidden on Mobile */}
      <button
        onClick={scrollPrev}
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-[#FDB714] hover:bg-[#e5a510] p-3 rounded-full transition-colors z-10 shadow-lg items-center justify-center"
        aria-label="Previous"
      >
        <ChevronLeft className="text-black" size={24} />
      </button>
      <button
        onClick={scrollNext}
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-[#FDB714] hover:bg-[#e5a510] p-3 rounded-full transition-colors z-10 shadow-lg items-center justify-center"
        aria-label="Next"
      >
        <ChevronRight className="text-black" size={24} />
      </button>

      {/* Swipe Indicator for Mobile */}
      <div className="md:hidden text-center mt-4">
        <p className="text-gray-500 text-xs">← Kaydır →</p>
      </div>
    </div>
  );
};

export default SalonCarousel;
